import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import BackstageContacts from "@/components/backstage/backstage-contacts";
import { useOwnedTalents } from "@/hooks/use-owned-talents";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { TalentContact } from "@shared/schema";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Star,
  Mail,
  Phone,
  Search,
} from "lucide-react";

type ContactRole =
  | "sound_tech"
  | "driver"
  | "hair_makeup"
  | "venue_mgr"
  | "hotel"
  | "promoter_contact"
  | "tour_manager"
  | "opener"
  | "merch"
  | "photographer"
  | "caterer"
  | "security"
  | "other";

const ROLE_OPTIONS: Array<{ value: ContactRole; label: string }> = [
  { value: "sound_tech", label: "Sound Tech" },
  { value: "driver", label: "Driver" },
  { value: "hair_makeup", label: "Hair / Makeup" },
  { value: "venue_mgr", label: "Venue Manager" },
  { value: "hotel", label: "Hotel" },
  { value: "promoter_contact", label: "Promoter" },
  { value: "tour_manager", label: "Tour Manager" },
  { value: "opener", label: "Opener" },
  { value: "merch", label: "Merch" },
  { value: "photographer", label: "Photographer" },
  { value: "caterer", label: "Caterer" },
  { value: "security", label: "Security" },
  { value: "other", label: "Other" },
];

export default function TalentContactsPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <BackstageContacts />;
  return <TalentContactsDesktop />;
}

function TalentContactsDesktop() {
  const { toast } = useToast();
  const {
    ownedTalents,
    selectedTalentId,
    setSelectedTalentId,
    selectedTalent,
    isLoading: talentLoading,
  } = useOwnedTalents();

  const { data: contacts = [], isLoading: contactsLoading } = useQuery<
    TalentContact[]
  >({
    queryKey: ["/api/talent-contacts", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(
        `/api/talent-contacts?talentId=${encodeURIComponent(selectedTalentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed to load contacts");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  // ── Filters ──────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (contacts || [])
      .filter((c) => (favoritesOnly ? c.isFavorite : true))
      .filter((c) => (roleFilter === "all" ? true : c.role === roleFilter))
      .filter((c) => {
        if (!q) return true;
        return [c.name, c.company, c.email, c.phone]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [contacts, search, roleFilter, favoritesOnly]);

  // ── Dialog ───────────────────────────────────────────────────
  const [dialog, setDialog] = useState<{
    open: boolean;
    editing: TalentContact | null;
  }>({ open: false, editing: null });
  const [contactForm, setContactForm] = useState({
    name: "",
    role: "other" as ContactRole,
    email: "",
    phone: "",
    company: "",
    address: "",
    notes: "",
    isFavorite: false,
  });

  function openDialog(c: TalentContact | null) {
    if (c) {
      setContactForm({
        name: c.name || "",
        role: (c.role as ContactRole) || "other",
        email: c.email || "",
        phone: c.phone || "",
        company: c.company || "",
        address: c.address || "",
        notes: c.notes || "",
        isFavorite: c.isFavorite,
      });
    } else {
      setContactForm({
        name: "",
        role: "other",
        email: "",
        phone: "",
        company: "",
        address: "",
        notes: "",
        isFavorite: false,
      });
    }
    setDialog({ open: true, editing: c });
  }

  const upsertContact = useMutation({
    mutationFn: async () => {
      if (!selectedTalentId) throw new Error("no talent selected");
      if (dialog.editing) {
        const r = await apiRequest(
          "PATCH",
          `/api/talent-contacts/${dialog.editing.id}`,
          contactForm,
        );
        return r.json();
      }
      const r = await apiRequest("POST", "/api/talent-contacts", {
        talentId: selectedTalentId,
        ...contactForm,
      });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent-contacts", selectedTalentId],
      });
      setDialog({ open: false, editing: null });
      toast({ title: "Saved", description: "Contact saved." });
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to save contact",
        variant: "destructive",
      }),
  });

  const deleteContact = useMutation({
    mutationFn: async (contactId: string) => {
      await apiRequest("DELETE", `/api/talent-contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent-contacts", selectedTalentId],
      });
      toast({ title: "Removed", description: "Contact removed." });
    },
  });

  const toggleFavorite = useMutation({
    mutationFn: async (c: TalentContact) => {
      const r = await apiRequest("PATCH", `/api/talent-contacts/${c.id}`, {
        isFavorite: !c.isFavorite,
      });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent-contacts", selectedTalentId],
      });
    },
  });

  if (talentLoading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="loading-contacts"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Contacts</CardTitle>
            <CardDescription>
              You don&apos;t have a talent profile linked to your account yet.
              Ask an admin to link you to your talent record.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight"
            data-testid="text-contacts-title"
          >
            Contacts
          </h1>
          <p className="text-muted-foreground text-sm">
            {selectedTalent?.name || "Your"} address book — sound techs,
            drivers, venue and promoter contacts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ownedTalents.length > 1 && (
            <Select
              value={selectedTalentId ?? ""}
              onValueChange={(v) => setSelectedTalentId(v)}
            >
              <SelectTrigger className="w-[200px]" data-testid="select-talent">
                <SelectValue placeholder="Select talent" />
              </SelectTrigger>
              <SelectContent>
                {ownedTalents.map((t) => (
                  <SelectItem
                    key={t.id}
                    value={t.id}
                    data-testid={`option-talent-${t.id}`}
                  >
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => openDialog(null)}
            data-testid="button-add-contact"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Contact
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, company, email or phone"
                className="pl-8"
                data-testid="input-search-contacts"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger
                className="w-full sm:w-[180px]"
                data-testid="select-role-filter"
              >
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 shrink-0">
              <Switch
                id="fav-only"
                checked={favoritesOnly}
                onCheckedChange={setFavoritesOnly}
                data-testid="switch-favorites-only"
              />
              <Label htmlFor="fav-only" className="text-sm whitespace-nowrap">
                Favorites
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {contactsLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : filtered.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="text-no-contacts"
            >
              No contacts match your filters.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filtered.map((c) => (
                <div
                  key={c.id}
                  className="border rounded-lg p-3 space-y-2 hover-elevate"
                  data-testid={`card-contact-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate flex items-center gap-1">
                        {c.name}
                      </p>
                      <Badge variant="secondary" className="text-xs mt-1">
                        {ROLE_OPTIONS.find((r) => r.value === c.role)?.label ||
                          c.role}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => toggleFavorite.mutate(c)}
                        data-testid={`button-favorite-contact-${c.id}`}
                      >
                        <Star
                          className={
                            c.isFavorite
                              ? "h-3.5 w-3.5 fill-yellow-400 text-yellow-400"
                              : "h-3.5 w-3.5 text-muted-foreground"
                          }
                        />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openDialog(c)}
                        data-testid={`button-edit-contact-${c.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => deleteContact.mutate(c.id)}
                        data-testid={`button-delete-contact-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {c.company && (
                    <p className="text-xs text-muted-foreground">{c.company}</p>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs">
                    {c.email && (
                      <a
                        href={`mailto:${c.email}`}
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <Mail className="h-3 w-3" />
                        {c.email}
                      </a>
                    )}
                    {c.phone && (
                      <a
                        href={`tel:${c.phone}`}
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <Phone className="h-3 w-3" />
                        {c.phone}
                      </a>
                    )}
                  </div>
                  {c.notes && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {c.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialog.open}
        onOpenChange={(open) =>
          setDialog((s) => ({ ...s, open, editing: open ? s.editing : null }))
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialog.editing ? "Edit Contact" : "Add Contact"}
            </DialogTitle>
            <DialogDescription>
              People you can quickly drop onto a call sheet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={contactForm.name}
                onChange={(e) =>
                  setContactForm({ ...contactForm, name: e.target.value })
                }
                data-testid="input-contact-name"
              />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select
                value={contactForm.role}
                onValueChange={(v) =>
                  setContactForm({ ...contactForm, role: v as ContactRole })
                }
              >
                <SelectTrigger data-testid="select-contact-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  value={contactForm.email}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, email: e.target.value })
                  }
                  data-testid="input-contact-email"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  value={contactForm.phone}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, phone: e.target.value })
                  }
                  data-testid="input-contact-phone"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-company">Company</Label>
              <Input
                id="contact-company"
                value={contactForm.company}
                onChange={(e) =>
                  setContactForm({ ...contactForm, company: e.target.value })
                }
                data-testid="input-contact-company"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-address">Address</Label>
              <Input
                id="contact-address"
                value={contactForm.address}
                onChange={(e) =>
                  setContactForm({ ...contactForm, address: e.target.value })
                }
                data-testid="input-contact-address"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={contactForm.notes}
                onChange={(e) =>
                  setContactForm({ ...contactForm, notes: e.target.value })
                }
                data-testid="input-contact-notes"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="contact-fav"
                checked={contactForm.isFavorite}
                onCheckedChange={(v) =>
                  setContactForm({ ...contactForm, isFavorite: v })
                }
                data-testid="switch-contact-favorite"
              />
              <Label htmlFor="contact-fav" className="text-sm">
                Mark as favorite
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialog({ open: false, editing: null })}
              data-testid="button-cancel-contact"
            >
              Cancel
            </Button>
            <Button
              onClick={() => upsertContact.mutate()}
              disabled={!contactForm.name.trim() || upsertContact.isPending}
              data-testid="button-save-contact"
            >
              {upsertContact.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
